import React, { useContext } from "react";
import Head from "next/head";
import { AppContext } from "context/app";
import { CartContext } from "context/cart";
import { UserContext } from "context/user";
import { ModalContext } from "context/modal";
import { LogContext } from "context/log";
import { ZipCodeContext } from "context/zipCode";

import { Cart as PepCart } from "@pepdirect/ui/cart";
import { Spinner } from "@pepdirect/ui/spinner";
import { EmptyCart } from "@pepdirect/ui/cart/EmptyCart";
import { getCheckoutUrl } from "helpers/cart";
import { logCartCheckoutButtonClickEvent } from "@pepdirect/helpers/analyticsLogger";
import { cartHasSubscriptionItem } from "helpers/cart";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import { cart as cartConfig, endpoints } from "config";
import st from "./cart.module.scss";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";

export default function Cart(): JSX.Element {
  const { tenant } = useContext(AppContext);
  const { cart, loading, getSize, updateCartItem } = useContext(CartContext);
  const { currentUserId } = useContext(UserContext);
  const { open } = useContext(ModalContext);
  const { blockedItems } = useContext(ZipCodeContext);
  const { useLogPageView } = useContext(LogContext);

  useLogPageView(
    {
      pageTitle: `Cart – ${META_TITLE}`,
      pageCategory: undefined,
      pageType: PAGE_TYPE_ENUM.cart,
      id: cart?.id || "",
    },
    undefined,
    cart
  );

  const checkoutUrl = getCheckoutUrl();
  const size = getSize();

  const { maxCartQty: maxCartQtyDefault } = cartConfig;
  const maxCartQty =
    tenant?.shoppingBagConfig?.maxAmountForSelect || maxCartQtyDefault;
  const minPurchaseAmt = tenant?.shoppingBagConfig?.minimumPurchaseAmount;
  const freeShippingThreshold = tenant?.shippingConfig?.freeShippingThreshold;

  const goToCheckout = () => {
    if (!cart) return;
    logCartCheckoutButtonClickEvent(currentUserId, cart);

    if (currentUserId === null) {
      const modal = cartHasSubscriptionItem(cart)
        ? "CHECKOUT_SUBSCRIPTION_MODAL"
        : "CHECKOUT_MODAL";
      open(modal);
    } else {
      window.open(checkoutUrl, "_self");
    }
  };

  if (loading) {
    return (
      <div className={st.loading}>
        <Spinner dotSize="25px" />
      </div>
    );
  }

  return (
    <div>
      <Head>
        <title>Cart – {META_TITLE}</title>
        <link rel="canonical" href={`${CANONICAL_URL_BASE}/cart`} />
      </Head>

      {size === 0 || !cart ? (
        <EmptyCart cdnEndpoint={endpoints.cdn} showTitle />
      ) : (
        <PepCart
          size={size}
          cart={cart}
          blockedItems={blockedItems}
          currentUserId={currentUserId}
          maxCartQty={maxCartQty}
          productsImgCdnEndpoint={endpoints.productsImgCdn}
          minPurchaseAmt={minPurchaseAmt}
          freeShippingThreshold={freeShippingThreshold}
          updateCartItem={updateCartItem}
          goToCheckout={goToCheckout}
        />
      )}
    </div>
  );
}
